import { camelCase } from 'lodash';
import React, { useImperativeHandle, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { twMerge } from 'tailwind-merge';

import { Telemetry } from '@videoblocks/kafka-rest-client';
import { Sparkles } from '@videoblocks/react-icons';

import { Text } from '../../../../../builder-io/storyblocks-design-system/components/atoms/Text/Text';
import { selectCreditBalance } from '../../../../ContentEdit/ContentEditSlice';
import GenerateButton from './GenerateButton';
import ReferenceImageUpload from './ReferenceImageUpload';
import { useVideoUI } from './context/VideoEditorContext';

interface AIPromptInputProps {
  onGenerateVideo: () => void;
  suggestions: string[];
  defaultPlaceholder?: string;
}

export interface AIPromptInputRef {
  getPromptValue: () => string;
  setPromptPlaceholderValue: (value: string) => void;
  setPromptValue: (value: string) => void;
}

const MAX_TEXTAREA_LENGTH = 1000;

const AIPromptInput: React.FC<AIPromptInputProps> = ({
  onGenerateVideo,
  suggestions = [],
  defaultPlaceholder = '',
}) => {
  const { promptInputRef, promptText } = useVideoUI();
  const creditBalance = useSelector(selectCreditBalance);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [currentLength, setCurrentLength] = useState(promptText?.length || 0);

  const getPromptValue = (): string => {
    return textareaRef.current?.value || '';
  };

  const setPromptValue = (value: string): void => {
    if (textareaRef.current) {
      textareaRef.current.value = value;
      setCurrentLength(value.length);
    }
  };

  const setPromptPlaceholderValue = (value: string): void => {
    if (textareaRef.current) {
      textareaRef.current.placeholder = value;
    }
  };

  useImperativeHandle(promptInputRef, () => ({
    getPromptValue,
    setPromptPlaceholderValue,
    setPromptValue,
  }));

  const handleSuggestionClick = (suggestion: string) => {
    setPromptPlaceholderValue(suggestion);
    Telemetry.increment(`ai.contentEdit.suggestion.${camelCase(suggestion)}`);
  };

  const isTextTooLong = currentLength > MAX_TEXTAREA_LENGTH;

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setCurrentLength(e.target.value.length);
  };

  return (
    <>
      <div
        className={twMerge(
          'w-full h-full flex flex-col justify-between bg-gray-900 border-2 border-gray-700 rounded-lg text-white',
          isTextTooLong && 'border-red-500'
        )}
      >
        <div className="p-4 h-full flex flex-col justify-between">
          {/* Textarea */}
          <textarea
            ref={textareaRef}
            placeholder={
              defaultPlaceholder || 'Describe the video you want to create...'
            }
            maxLength={MAX_TEXTAREA_LENGTH + 100} // Allow typing beyond limit for UX
            className="w-full h-full resize-none bg-transparent border-0 outline-none placeholder-gray-400 text-sm"
            onChange={handleTextChange}
            aria-label="Describe the video you want to create"
            aria-invalid={isTextTooLong}
            aria-describedby={isTextTooLong ? 'prompt-error' : 'prompt-count'}
            defaultValue={promptText}
          />

          <div className="flex justify-end items-center gap-3 mt-3">
            {/* Reference Image Upload */}
            <div className="mr-auto">
              <ReferenceImageUpload />
            </div>

            <Text fontSize="xs" className="text-white m-0" fontWeight="regular">
              {creditBalance} credit{creditBalance !== 1 ? 's' : ''} available
            </Text>
            <GenerateButton
              disabled={isTextTooLong}
              onGenerateVideo={onGenerateVideo}
            />
          </div>
        </div>

        {/* Suggestions Section */}
        <div className="flex gap-2 border-t border-gray-700 px-4 py-2 items-center">
          <Sparkles className="w-4 h-4 fill-white" />
          <Text fontSize="xs" className="text-white m-0">
            Suggestions:
          </Text>
          <div className="flex overflow-x-auto scrollbar-hide gap-2">
            {suggestions.length > 0 ? (
              suggestions.map((suggestion, index) => (
                <button
                  key={`suggestion-${index}`}
                  type="button"
                  onClick={() => handleSuggestionClick(suggestion)}
                  className="flex-shrink-0 bg-white text-gray-900 px-3 py-1.5 rounded-full text-xs transition-all hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50"
                  aria-label={`Use suggestion: ${suggestion}`}
                >
                  {suggestion}
                </button>
              ))
            ) : (
              <Text fontSize="xs" className="text-gray-400 italic m-0">
                No suggestions available
              </Text>
            )}
          </div>
        </div>
      </div>

      <div className="w-full">
        {!isTextTooLong ? (
          <Text
            fontSize="xs"
            fontWeight="regular"
            className="text-gray-400 m-0"
          >
            {currentLength}/{MAX_TEXTAREA_LENGTH} characters
          </Text>
        ) : (
          <Text className="text-red-500 m-0" fontSize="xs">
            Prompt exceeds {MAX_TEXTAREA_LENGTH} character limit. Please shorten
            to continue.
          </Text>
        )}
      </div>
    </>
  );
};

export default React.memo(AIPromptInput);
